import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link, useSearchParams } from 'react-router-dom';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import {
  Search, Filter, ArrowDownAZ, ArrowUpZA, MoreHorizontal,
  Mail, Phone, Plus, X, Pencil, Wifi, Calendar, ExternalLink,
  Tag as TagIcon, Trash2,
} from 'lucide-react';

const DESIGNATIONS = ['CEO', 'CTO', 'CFO', 'COO', 'VP Sales', 'VP Marketing', 'Director', 'Manager', 'Team Lead', 'Executive', 'Analyst', 'Other'];
const DEPARTMENTS = ['Executive', 'Sales', 'Marketing', 'Engineering', 'Product', 'Finance', 'HR', 'Operations', 'Support', 'Other'];

// Subtle theme-aligned avatar tones — soft pastels with darker text, not solid
// rainbow blocks. Matches the unified slate-based aesthetic. Hash-derived so
// each contact gets a stable but quiet identity.
const AVATAR_TONES = [
  'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
];
function avatarToneFor(name) {
  if (!name) return AVATAR_TONES[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}
function initialsOf(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase();
}

function Avatar({ name, size = 'md' }) {
  const cls = size === 'lg' ? 'w-10 h-10 text-sm' : size === 'sm' ? 'w-7 h-7 text-[10px]' : 'w-9 h-9 text-xs';
  return (
    <div className={`${avatarToneFor(name)} ${cls} rounded-full flex items-center justify-center font-semibold flex-shrink-0`}>
      {initialsOf(name)}
    </div>
  );
}

function ContactRow({ contact, isSelected, onSelect, onDelete }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(contact)}
      data-testid={`contact-row-${contact.id}`}
      className={`w-full flex items-center gap-4 px-5 py-3 border-b border-slate-100 dark:border-slate-800 text-left transition-colors group ${
        isSelected
          ? 'bg-slate-100 dark:bg-slate-800'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }`}
    >
      <Avatar name={contact.name} />
      <div className="min-w-0 flex-1 grid grid-cols-12 gap-3 items-center">
        <div className="col-span-4 truncate">
          <div className="font-medium text-slate-900 dark:text-slate-100 truncate text-sm">{contact.name || '—'}</div>
        </div>
        <div className="col-span-4 truncate">
          <span className="text-sm text-slate-500 dark:text-slate-400 truncate">{contact.email || ''}</span>
        </div>
        <div className="col-span-2 truncate">
          <span className="text-sm text-slate-500 dark:text-slate-400 truncate">{contact.phone || ''}</span>
        </div>
        <div className="col-span-2 truncate">
          <span className="text-sm text-slate-500 dark:text-slate-400 truncate">{contact.company_name || ''}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-white transition-colors"
            title={`Call ${contact.phone}`}
            data-testid={`row-call-${contact.id}`}
          >
            <Phone className="w-4 h-4" />
          </a>
        )}
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-white transition-colors"
            title={`Email ${contact.email}`}
            data-testid={`row-email-${contact.id}`}
          >
            <Mail className="w-4 h-4" />
          </a>
        )}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onDelete(contact); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onDelete(contact); } }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          title="Delete contact"
          data-testid={`row-delete-${contact.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </span>
      </div>
    </button>
  );
}

function DetailPanel({ contact, intel, onEdit, onDelete }) {
  if (!contact) {
    return (
      <aside className="w-96 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 flex items-center justify-center text-center">
        <div className="text-slate-400 dark:text-slate-500 text-sm">
          <Avatar name="?" size="lg" />
          <p className="mt-4">Select a contact to view their details</p>
        </div>
      </aside>
    );
  }

  const rel = intel?.relationship || {};
  const positions = intel?.positions || [];
  const primaryCompany = positions.find((p) => p.is_current) || positions[0];

  return (
    <aside className="w-96 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto" data-testid="contact-detail-panel">
      {/* Identity + primary actions — always visible */}
      <div className="p-5 flex items-start justify-between gap-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={contact.name} size="lg" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid="detail-contact-name">
              {contact.name || '—'}
            </h2>
            {contact.designation && (
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{contact.designation}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-2 rounded-md text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Edit contact"
            data-testid="detail-edit-icon-btn"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-md text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            title="Delete contact"
            data-testid="detail-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Header — company badge */}
      {primaryCompany && (
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center font-bold text-sm flex-shrink-0">
            {initialsOf(primaryCompany.company_name)}
          </div>
          <Link
            to={`/companies/${primaryCompany.company_id}`}
            className="font-medium text-slate-800 dark:text-slate-200 hover:underline truncate flex items-center gap-1"
          >
            {primaryCompany.company_name}
            <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
          </Link>
        </div>
      )}

      {/* Contact details */}
      <section className="p-5 border-b border-slate-100 dark:border-slate-800">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">Contact details</h3>
        {contact.email && (
          <div className="flex items-center gap-2 text-sm mb-2">
            <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-slate-700 dark:text-slate-300 truncate">{contact.email}</span>
            <span className="text-xs text-slate-400 ml-1">| work</span>
          </div>
        )}
        {contact.phone && (
          <div className="flex items-center gap-2 text-sm mb-2">
            <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-slate-700 dark:text-slate-300">{contact.phone}</span>
          </div>
        )}
        <button
          onClick={onEdit}
          className="text-slate-700 dark:text-slate-200 hover:underline text-sm font-medium mt-2"
          data-testid="detail-edit-btn"
        >
          Edit
        </button>
      </section>

      {/* Relationship */}
      <section className="p-5 border-b border-slate-100 dark:border-slate-800">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">Relationship</h3>
        {rel.strongest_connection ? (
          <div className="flex items-center gap-2 text-sm mb-3">
            <Wifi className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-slate-600 dark:text-slate-400">Strongest connection with</span>
            <span className="text-slate-900 dark:text-slate-100 font-medium">{rel.strongest_connection.name}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm mb-3">
            <Wifi className="w-4 h-4 text-slate-300 flex-shrink-0" />
            <span className="text-slate-400 dark:text-slate-500">No connections yet</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm mb-2" data-testid="detail-emails-stat">
          <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-slate-700 dark:text-slate-300">
            {rel.emails_sent || 0} sent, {rel.emails_received || 0} received
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm mb-2" data-testid="detail-meetings-stat">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-slate-700 dark:text-slate-300">{rel.meetings || 0} meetings</span>
        </div>
        <div className="flex items-center gap-2 text-sm" data-testid="detail-calls-stat">
          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-slate-700 dark:text-slate-300">{rel.calls || 0} calls</span>
        </div>
      </section>

      {/* Positions */}
      {positions.length > 0 && (
        <section className="p-5 border-b border-slate-100 dark:border-slate-800">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">Positions</h3>
          {positions.map((p, idx) => (
            <div key={`${p.company_id}-${idx}`} className="mb-2 last:mb-0">
              <Link
                to={`/companies/${p.company_id}`}
                className="text-slate-800 dark:text-slate-200 hover:underline text-sm font-medium"
              >
                {p.company_name}
              </Link>
              {p.designation && (
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{p.designation}</div>
              )}
            </div>
          ))}
          <button
            onClick={onEdit}
            className="text-slate-700 dark:text-slate-200 hover:underline text-sm font-medium mt-2"
          >
            Edit
          </button>
        </section>
      )}

      {/* Tags */}
      <section className="p-5">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2">
          <TagIcon className="w-4 h-4" /> Tags
        </h3>
        <p className="text-sm text-slate-400 dark:text-slate-500">No tags available.</p>
      </section>
    </aside>
  );
}

export default function ContactsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const batchFilter = useMemo(() => searchParams.get('batch') || '', [searchParams]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Default to "newest" when arriving via an import deep-link so the user
  // sees freshly-imported rows first instead of hunting alphabetically.
  const [sort, setSort] = useState(batchFilter ? 'newest' : 'asc');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState(emptyForm());
  const limit = 50;

  function emptyForm() {
    return {
      company_id: '', name: '', email: '', phone: '',
      designation: '', department: '', linkedin_url: '',
      is_primary: false, is_decision_maker: false, influence_level: 'medium', notes: '',
    };
  }

  // ─── Fetch list ────────────────────────────────────────────────────────────
  const fetchContacts = useCallback(async (resetSelected = false) => {
    setLoading(true);
    try {
      const params = { page, limit, sort };
      if (searchTerm) params.search = searchTerm;
      if (batchFilter) params.import_batch_id = batchFilter;
      const res = await axios.get(`${API}/contacts/my`, { params });
      const list = res.data?.contacts || [];
      setContacts(list);
      setTotal(res.data?.total || 0);
      setHasMore(!!res.data?.has_more);
      // Auto-pick first if nothing selected (or after search reset)
      if (resetSelected || !selected) {
        if (list.length > 0) setSelected(list[0]);
        else setSelected(null);
      }
    } catch (e) {
      console.error('Failed to fetch contacts:', e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sort, searchTerm, batchFilter]);

  const fetchCompanies = useCallback(async () => {
    try {
      // Lazy-loaded only when the create/edit modal opens (was eager 2000-row
      // pull on every page mount). Cap at 100 — for larger orgs the dropdown
      // should be replaced with an AsyncSearchSelect typeahead.
      const res = await axios.get(`${API}/companies?limit=100`);
      const list = res.data?.data || res.data?.companies || (Array.isArray(res.data) ? res.data : []);
      setCompanies(list);
    } catch (e) { console.error('companies fetch failed', e); }
  }, []);

  useEffect(() => { fetchContacts(true); }, [fetchContacts]);
  // Lazy: only fetch companies when the user actually opens the create/edit modal.
  useEffect(() => {
    if (showAddModal || editingContact) {
      if (companies.length === 0) fetchCompanies();
    }
  }, [showAddModal, editingContact, companies.length, fetchCompanies]);

  // ─── Realtime ──────────────────────────────────────────────────────────────
  useRealtimeNotifications((evt) => {
    if (evt?.type === 'graph_change' && evt?.kind === 'contact') fetchContacts();
  });

  // ─── Intel for selected ────────────────────────────────────────────────────
  useEffect(() => {
    if (!selected?.id) { setIntel(null); return; }
    let cancelled = false;
    (async () => {
      setIntelLoading(true);
      try {
        const res = await axios.get(`${API}/contacts/${selected.id}/intel`);
        if (!cancelled) setIntel(res.data);
      } catch (e) {
        if (!cancelled) setIntel(null);
      } finally {
        if (!cancelled) setIntelLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  // ─── Search debounce ───────────────────────────────────────────────────────
  const searchDebounce = useRef(null);
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput);
    }, 300);
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, [searchInput]);

  // ─── Modal/Form handlers ───────────────────────────────────────────────────
  const openCreate = () => { setEditingContact(null); setFormData(emptyForm()); setShowAddModal(true); };
  const openEdit = (c) => {
    setEditingContact(c);
    setFormData({
      company_id: c.company_id || '', name: c.name || '', email: c.email || '',
      phone: c.phone || '', designation: c.designation || '', department: c.department || '',
      linkedin_url: c.linkedin_url || c.linkedin || '',
      is_primary: c.is_primary || c.is_primary_contact || false,
      is_decision_maker: c.is_decision_maker || false,
      influence_level: c.influence_level || 'medium', notes: c.notes || '',
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let saved;
      if (editingContact) {
        const res = await axios.put(`${API}/contacts/${editingContact.id}`, formData);
        saved = res.data;
        setMessage({ type: 'success', text: 'Contact updated' });
      } else {
        const res = await axios.post(`${API}/contacts`, formData);
        saved = res.data;
        setMessage({ type: 'success', text: 'Contact created' });
      }
      setShowAddModal(false);
      setEditingContact(null);
      await fetchContacts();
      if (saved?.id) setSelected(saved);
      setTimeout(() => setMessage({ type: '', text: '' }), 2500);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save' });
    }
  };

  const handleDelete = async (contactArg) => {
    // Accepts an optional contact to delete. If omitted, falls back to the
    // currently edited contact (legacy call site from the edit modal).
    const target = contactArg || editingContact;
    if (!target) return;
    if (!window.confirm(`Delete contact "${target.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/contacts/${target.id}`);
      if (editingContact?.id === target.id) {
        setShowAddModal(false);
        setEditingContact(null);
      }
      if (selected?.id === target.id) setSelected(null);
      await fetchContacts(true);
      setMessage({ type: 'success', text: 'Contact deleted' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2500);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete' });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-4rem)] bg-white dark:bg-slate-950" data-testid="contacts-page-v2">
      {/* MAIN — list */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top header — tabs + toolbar */}
        <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <div className="flex items-center gap-1">
              <button
                className="px-3 pb-3 text-sm font-medium text-slate-900 dark:text-slate-100 border-b-2 border-slate-900 dark:border-white"
                data-testid="tab-my-contacts"
              >
                My contacts
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded bg-slate-900 dark:bg-white text-white dark:text-slate-900">
                  {total}
                </span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name, email, phone or company"
                  className="w-72 pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:bg-white dark:focus:bg-slate-900"
                  data-testid="contacts-search-input"
                />
              </div>
              <button
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500"
                title="Filter"
                data-testid="contacts-filter-btn"
              >
                <Filter className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setSort(sort === 'asc' ? 'desc' : 'asc'); setPage(1); }}
                className="flex items-center gap-1 px-2.5 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 text-xs font-semibold uppercase"
                title="Toggle sort"
                data-testid="contacts-sort-toggle"
              >
                {sort === 'asc' ? <ArrowDownAZ className="w-4 h-4" /> : <ArrowUpZA className="w-4 h-4" />}
                A-Z
              </button>
              <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500" title="More">
                <MoreHorizontal className="w-4 h-4" />
              </button>
              <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                data-testid="contacts-add-btn"
              >
                <Plus className="w-4 h-4" />
                Add contact
              </button>
            </div>
          </div>

          {/* Sub-header: count + sort caption */}
          <div className="px-6 pb-3 text-xs text-slate-500 dark:text-slate-400" data-testid="contacts-count-caption">
            {loading ? 'Loading…' : `${total} contacts by ${sort === 'newest' ? 'newest first' : sort === 'oldest' ? 'oldest first' : 'name'} ${sort === 'asc' || sort === 'desc' ? `(${sort})` : ''}`}
          </div>
        </header>

        {/* Import-batch filter banner */}
        {batchFilter && (
          <div className="mx-6 mt-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex items-center justify-between gap-3 text-xs text-blue-800 dark:text-blue-200" data-testid="import-batch-banner">
            <div className="flex items-center gap-2">
              <Wifi className="w-3.5 h-3.5" />
              <span>Filtered to contacts from your recent import · <strong>{total}</strong> record{total === 1 ? '' : 's'}</span>
            </div>
            <button
              onClick={() => { setSearchParams({}); setPage(1); }}
              className="px-2 py-1 text-[11px] font-medium rounded bg-white dark:bg-slate-800 border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40"
              data-testid="clear-batch-filter"
            >
              Show all contacts
            </button>
          </div>
        )}

        {/* Toast */}
        {message.text && (
          <div className={`mx-6 mt-3 px-4 py-2 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-rose-50 text-rose-700 border border-rose-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto" data-testid="contacts-list-container">
          {loading && contacts.length === 0 && (
            <div className="p-12 text-center text-slate-400">Loading contacts…</div>
          )}
          {!loading && contacts.length === 0 && (
            <div className="p-12 text-center text-slate-400" data-testid="contacts-empty-state">
              <Avatar name="?" size="lg" />
              <p className="mt-4 text-sm">
                {searchTerm ? 'No contacts match your search' : "You haven't created any contacts yet"}
              </p>
              {!searchTerm && (
                <button
                  onClick={openCreate}
                  className="mt-4 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium"
                >
                  Add your first contact
                </button>
              )}
            </div>
          )}
          {contacts.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              isSelected={selected?.id === c.id}
              onSelect={setSelected}
              onDelete={handleDelete}
            />
          ))}
          {/* Pagination */}
          {(page > 1 || hasMore) && contacts.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-sm">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="contacts-prev-btn"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page} • Showing {(page - 1) * limit + 1}–{(page - 1) * limit + contacts.length} of {total}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
                className="px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="contacts-next-btn"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* RIGHT — detail panel */}
      <DetailPanel
        contact={selected}
        intel={intelLoading ? null : intel}
        onEdit={() => selected && openEdit(selected)}
        onDelete={() => selected && handleDelete(selected)}
      />

      {/* Add/Edit modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-modal"
          >
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {editingContact ? 'Edit Contact' : 'Add New Contact'}
              </h3>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Full Name *</label>
                <input
                  type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                  data-testid="modal-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Email</label>
                  <input
                    type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                    data-testid="modal-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Phone</label>
                  <input
                    type="tel" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                    data-testid="modal-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Company</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                  data-testid="modal-company-select"
                >
                  <option value="">—</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Designation</label>
                  <select
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                  >
                    <option value="">—</option>
                    {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">Department</label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                  >
                    <option value="">—</option>
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">LinkedIn URL</label>
                <input
                  type="url" value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100"
                />
              </div>
              <div className="flex items-center gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={formData.is_primary} onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })} />
                  Primary contact
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={formData.is_decision_maker} onChange={(e) => setFormData({ ...formData, is_decision_maker: e.target.checked })} />
                  Decision maker
                </label>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700 -mx-5 px-5">
                <div>
                  {editingContact && (
                    <button type="button" onClick={handleDelete} className="text-sm text-rose-600 hover:underline" data-testid="modal-delete-btn">
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium" data-testid="modal-save-btn">
                    {editingContact ? 'Save Changes' : 'Create Contact'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
